import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { DetailPageLayout } from '@/components/crm/DetailPageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EntityQuotes } from '@/components/crm/quote/EntityQuotes';
import { EntityAppointments } from '@/components/crm/EntityAppointments';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import { 
  Edit, 
  Trash2, 
  MapPin, 
  Building2, 
  FileText, 
  DollarSign, 
  Zap, 
  Ruler, 
  Users,
  ExternalLink,
  Plus,
  Truck,
  Presentation,
  Moon,
  Sun,
  Archive
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LocationModal } from '@/components/charging/LocationModal';
import { LocationSlideGenerator } from '@/components/charging/LocationSlideGenerator';
import { SiteSketchUploader } from '@/components/charging/SiteSketchUploader';
import { useMapWithProvider } from '@/hooks/useMapWithProvider';
import { LocationActivityLog } from '@/components/charging/LocationActivityLog';
import { LocationTimeline } from '@/components/charging/LocationTimeline';
import { LocationNotes } from '@/components/charging/LocationNotes';
import { LocationTodos } from '@/components/charging/LocationTodos';
import { LocationBusinesscaseFiles } from '@/components/charging/LocationBusinesscaseFiles';
import { LocationDocuments } from '@/components/charging/LocationDocuments';
import { LocationDocumentsPreview } from '@/components/charging/LocationDocumentsPreview';
import {
  KAVEL_FASEN,
  STADIA_PER_FASE,
  faseInfo,
  faseVanStadium,
  getStageGroup,
  kavelZicht,
  stadiumLabel,
} from '@/lib/chargingStages';
import { useKavelSlaap } from '@/hooks/useKavelSlaap';
import { logUnauthorizedAccessAttempt } from '@/lib/securityLogger';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STAGE_COLORS = {
  acquisitie: '#0091ff',  // Blauw
  realisatie: '#ff9100',   // Oranje
  operatie: '#28a745',     // Groen
  verloren: '#e53935',     // Rood
} as const;

export default function ChargingLocationDetail() {
  const { locationId } = useParams();
  const navigate = useNavigate();
  const { MapComponent } = useMapWithProvider();
  const location = useLocation();
  const { toast } = useToast();
  const { user, userRole } = useAuth();
  const queryClient = useQueryClient();
  
  const [locationData, setLocationData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSlideGeneratorOpen, setIsSlideGeneratorOpen] = useState(false);
  const [linkedBusinesscases, setLinkedBusinesscases] = useState<any[]>([]);
  const [interestedParties, setInterestedParties] = useState<any[]>([]);
  const { slaapIn, maakWakker, archiveerAsync, wijzig, bezig: slaapBezig } = useKavelSlaap();

  // CRITICAL Security check: prevent charging_user from accessing CRM routes
  if (userRole === 'charging_user' && location.pathname.includes('/crm/')) {
    console.warn('[ChargingLocationDetail] Unauthorized CRM access attempt:', {
      userRole,
      path: location.pathname,
      userId: user?.id
    });
    
    // Show toast to user
    toast({
      title: 'Geen toegang',
      description: 'Je hebt geen toegang tot CRM functionaliteit. Je wordt doorgestuurd naar de Charging portal.',
      variant: 'destructive'
    });
    
    // Log unauthorized access attempt for security auditing
    if (user?.id) {
      logUnauthorizedAccessAttempt(
        user.id,
        user.user_metadata?.tenant_id || null,
        {
          attempted_url: location.pathname,
          user_role: userRole,
          user_agent: navigator.userAgent,
          referrer: document.referrer,
          redirect_to: '/charging'
        }
      ).catch(console.error);
    }
    
    navigate('/charging', { replace: true });
    return null;
  }

  useEffect(() => {
    console.log('[ChargingLocationDetail] Component mounted', {
      locationId,
      userRole,
      pathname: location.pathname,
      userEmail: user?.email
    });
    if (locationId) fetchLocationData();
  }, [locationId]);

  // Realtime updates voor deze specifieke locatie
  useEffect(() => {
    if (!locationId) return;

    const channel = supabase
      .channel(`location-${locationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'charging_locations',
          filter: `id=eq.${locationId}`,
        },
        (payload) => {
          console.log('Location updated:', payload);
          fetchLocationData();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'charging_locations',
          filter: `id=eq.${locationId}`,
        },
        () => {
          toast({ 
            title: 'Locatie is verwijderd',
            description: 'Deze locatie bestaat niet meer'
          });
          const backUrl = userRole === 'charging_user' ? '/charging' : '/crm/charging';
          navigate(backUrl);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [locationId, userRole, navigate]);

  const fetchLocationData = async () => {
    setLoading(true);
    
    try {
      console.log('[ChargingLocationDetail] Fetching location:', {
        locationId,
        userRole,
        userId: user?.id,
        pathname: location.pathname
      });

      // Geen `.is('deleted_at', null)` meer (T2.5): een gearchiveerde Kavel
      // bestond wél maar gaf hier "niet gevonden", zodat een deep-link vanaf
      // het Archief-scherm in een lege pagina eindigde. Nu laadt hij, met een
      // balk bovenaan die zegt in welke stand hij staat.
      const { data: locData, error } = await supabase
        .from('charging_locations')
        .select('*, organizations(id, name)')
        .eq('id', locationId)
        .maybeSingle();
      
      if (error) {
        console.error('[ChargingLocationDetail] Query error:', error);
        toast({ 
          title: 'Database fout', 
          description: `Fout bij ophalen locatie: ${error.message}`,
          variant: 'destructive' 
        });
        return;
      }
      
      if (!locData) {
        console.warn('[ChargingLocationDetail] Location not found');
        toast({ 
          title: 'Locatie niet gevonden', 
          description: 'Deze locatie bestaat niet of je hebt geen toegang',
          variant: 'destructive' 
        });
        const backUrl = userRole === 'charging_user' ? '/charging' : '/crm/charging';
        navigate(backUrl);
        return;
      }
      
      console.log('[ChargingLocationDetail] Location loaded:', locData);
      setLocationData(locData);

      // Fetch interested parties via opportunity_locations
      const { data: oppLinks } = await supabase
        .from('opportunity_locations')
        .select(`
          id,
          interest_level,
          trucks_per_day,
          opportunity:charging_opportunities(
            id,
            name,
            organization:organizations(id, name, logo_url, favicon_url, website)
          )
        `)
        .eq('location_id', locationId);

      if (oppLinks) {
        const parties = oppLinks.map((link: any) => ({
          id: link.id,
          opportunityId: link.opportunity?.id,
          opportunityName: link.opportunity?.name,
          orgName: link.opportunity?.organization?.name,
          orgLogo: link.opportunity?.organization?.logo_url || link.opportunity?.organization?.favicon_url,
          interestLevel: link.interest_level,
          trucks: link.trucks_per_day || 0,
        })).filter((p: any) => p.opportunityId);
        
        setInterestedParties(parties);
      }
    } catch (error: any) {
      console.error('[ChargingLocationDetail] Fetch error:', error);
      toast({ 
        title: 'Fout bij laden', 
        description: error.message,
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  // T2.5 / scepticus-ronde — archiveren loopt via `useKavelSlaap.archiveer`,
  // net als op de fase-schermen. Dit was de vierde kopie van dezelfde UPDATE,
  // met een eigen foutafhandeling die de PGRST204-vertaling miste en die
  // `dormant_at` onvoorwaardelijk meestuurde. De hook peilt of de kolom
  // bestaat en laat hem weg zolang migratie 084 niet is toegepast, zodat
  // archiveren werkt met én zonder die migratie.
  const handleDelete = async () => {
    try {
      await archiveerAsync(locationId!);

      queryClient.invalidateQueries({ queryKey: ['location-businesscase-files'] });
      queryClient.invalidateQueries({ queryKey: ['location-documents'] });

      const backUrl = userRole === 'charging_user' ? '/charging' : '/crm/charging';
      navigate(backUrl);
    } catch {
      // useKavelSlaap toont de foutmelding al, met het juiste onderwerp.
    }
  };

  // Dit is het enige werkende pad om een Kavel met een onbekend stadium te
  // repareren — precies waar de tekst op het Archief-scherm naartoe wijst.
  // Het telde tot de scepticus-ronde geen rijen en meldde altijd succes.
  const handleStageUpdate = async (newStage: string) => {
    try {
      await wijzig(locationId!, { stage: newStage }, 'Stadium wijzigen');

      toast({
        title: 'Stadium bijgewerkt',
        description: `Kavel verplaatst naar ${stadiumLabel(newStage)}`
      });

      // Reload location data
      fetchLocationData();
    } catch (error: any) {
      console.error('Stage update error:', error);
      toast({
        title: 'Fout bij status wijziging',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Vóór T2.5 stond hier een eigen lijst met zeven van de elf stadia. Een
  // Kavel in 'nieuw', 'uitwerking', 'contracting', 'voorlopig_ontwerp' of
  // 'verloren' viel daardoor in de `||`-tak en kreeg een grijze badge met de
  // ruwe waarde ("voorlopig_ontwerp"). De labels komen nu uit de enige lijst
  // die ze kent; de kleur van de badge volgt de fase, niet het stadium.
  const getStageInfo = (stage: string) => ({
    label: stadiumLabel(stage),
    color: faseInfo(faseVanStadium(stage)).dot,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">Laden...</div>
      </div>
    );
  }
  
  if (!locationData) return null;

  const stageInfo = getStageInfo(locationData.stage);
  const backUrl = userRole === 'charging_user' ? '/charging' : '/crm/charging';
  const zicht = kavelZicht(locationData);

  return (
    <DetailPageLayout
      title={locationData.name || 'Onbekende locatie'}
      subtitle={`${locationData.locode ? locationData.locode + ' • ' : ''}${locationData.organizations?.name || ''}`}
      backUrl={backUrl}
      actions={
        <>
          <Button variant="outline" onClick={() => setIsSlideGeneratorOpen(true)}>
            <Presentation className="w-4 h-4 mr-2" />
            Genereer Slide
          </Button>
          <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
            <Edit className="w-4 h-4 mr-2" />
            Bewerken
          </Button>
          {/* T2.5 — slapend zetten en terugzetten. Alleen zinvol zolang de
              Kavel niet gearchiveerd is: die twee standen sluiten elkaar uit. */}
          {zicht !== 'gearchiveerd' && (
            <Button
              variant="outline"
              disabled={slaapBezig}
              onClick={() => (zicht === 'slapend' ? maakWakker(locationId!) : slaapIn(locationId!))}
            >
              {zicht === 'slapend' ? (
                <>
                  <Sun className="w-4 h-4 mr-2" />
                  Wakker maken
                </>
              ) : (
                <>
                  <Moon className="w-4 h-4 mr-2" />
                  Slapend zetten
                </>
              )}
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="w-4 h-4 mr-2" />
                Verwijderen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Weet je het zeker?</AlertDialogTitle>
                <AlertDialogDescription>
                  Deze actie kan niet ongedaan worden gemaakt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>Verwijderen</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      }
    >
      {/* T2.5 — een Kavel die niet in de actieve lijst staat, zegt dat zelf.
          Zonder deze balk kun je een slapende of gearchiveerde Kavel openen,
          bewerken, en je afvragen waarom hij nergens opduikt. */}
      {zicht !== 'actief' && (
        <div
          className={
            zicht === 'slapend'
              ? 'mb-6 flex items-center gap-2 rounded-lg border border-dashed bg-muted/40 px-4 py-3 text-sm'
              : 'mb-6 flex items-center gap-2 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm'
          }
        >
          {zicht === 'slapend' ? <Moon className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          <span>
            {zicht === 'slapend'
              ? 'Deze Kavel slaapt: hij staat niet in de standaardlijst van zijn fase, maar telt nog wel als dossier. Eén klik op “Wakker maken” zet hem terug.'
              : 'Deze Kavel is gearchiveerd. Herstellen kan op het Archief-scherm.'}
          </span>
        </div>
      )}

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overzicht</TabsTrigger>
          <TabsTrigger value="businesscase">Businesscase</TabsTrigger>
          <TabsTrigger value="activity">Activiteiten</TabsTrigger>
          <TabsTrigger value="quotes">Offertes</TabsTrigger>
          <TabsTrigger value="documents">Documenten</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* New Layout: Left Column (320px) + Right Column (flex) */}
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
            {/* Left Column: Location Details */}
            <Card className="min-h-[450px]">
              <CardHeader className="py-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <MapPin className="w-4 h-4" />
                  Locatie Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <p className="font-medium text-xs text-muted-foreground">Status</p>
                  <Badge className={stageInfo.color}>{stageInfo.label}</Badge>
                </div>
                
                <div>
                  <p className="font-medium text-xs text-muted-foreground">Locatiecode</p>
                  {locationData.locode ? (
                    <span className="inline-block font-mono bg-primary/10 text-primary px-2 py-0.5 rounded text-xs">
                      {locationData.locode}
                    </span>
                  ) : (
                    <p className="text-muted-foreground">Geen code</p>
                  )}
                </div>
                
                {locationData.address && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground">Adres</p>
                    <p className="text-muted-foreground">{locationData.address}</p>
                  </div>
                )}
                
                {(locationData.postal_code || locationData.city) && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground">Postcode / Stad</p>
                    <p className="text-muted-foreground">
                      {[locationData.postal_code, locationData.city].filter(Boolean).join(', ')}
                    </p>
                  </div>
                )}
                
                {locationData.organizations && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground">Organisatie</p>
                    <Button 
                      variant="link" 
                      className="p-0 h-auto text-sm"
                      onClick={() => navigate(`/crm/organisaties/${locationData.org_id}`)}
                    >
                      {locationData.organizations.name}
                    </Button>
                  </div>
                )}

                {locationData.available_power_kw && (
                  <div className="flex items-center gap-2">
                    <Zap className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{locationData.available_power_kw} kW</span>
                  </div>
                )}

                {locationData.development_size_chargers && (
                  <div className="flex items-center gap-2">
                    <Users className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{locationData.development_size_chargers} laadpalen</span>
                  </div>
                )}

                {locationData.plot_size_m2 && (
                  <div className="flex items-center gap-2">
                    <Ruler className="w-3 h-3 text-muted-foreground" />
                    <span className="text-muted-foreground">{locationData.plot_size_m2} m²</span>
                  </div>
                )}

                {locationData.realtor_name && (
                  <div>
                    <p className="font-medium text-xs text-muted-foreground flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Makelaar
                    </p>
                    <p className="text-muted-foreground">{locationData.realtor_name}</p>
                  </div>
                )}

                {locationData.market_listing_url && (
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-sm"
                    onClick={() => window.open(locationData.market_listing_url, '_blank')}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Bekijk listing
                  </Button>
                )}
                
                <div>
                  <p className="font-medium text-xs text-muted-foreground">Aangemaakt</p>
                  <p className="text-muted-foreground">
                    {format(new Date(locationData.created_at), 'dd MMM yyyy')}
                  </p>
                </div>

                {/* Stage Selector */}
                {/* T2.5 / scepticus-ronde — was de zesde handgeschreven
                    stadia-lijst, met eigen labels ("Voorlopig Ontwerp" naast
                    "Voorlopig ontwerp" elders) en zonder de mogelijkheid om
                    een Kavel met een onbekend stadium te tonen: bij
                    `stage = 'prospect'` stond deze keuzelijst leeg, terwijl
                    dít het scherm is waar je die reparatie moet doen.
                    Nu gegroepeerd per fase, uit de gedeelde lijst. */}
                <div className="pt-3 border-t space-y-2">
                  <Label className="text-xs font-medium">Stadium wijzigen</Label>
                  <Select
                    value={locationData?.stage || ''}
                    onValueChange={handleStageUpdate}
                  >
                    <SelectTrigger className="w-full h-8 text-xs">
                      <SelectValue placeholder="Selecteer stadium" />
                    </SelectTrigger>
                    <SelectContent>
                      {KAVEL_FASEN.map((fase) => (
                        <SelectGroup key={fase.id}>
                          <SelectLabel>{fase.label}</SelectLabel>
                          {STADIA_PER_FASE[fase.id].map((stadium) => (
                            <SelectItem key={stadium} value={stadium}>
                              {stadiumLabel(stadium)}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                      {faseVanStadium(locationData?.stage) === 'onbekend' && locationData?.stage && (
                        <SelectGroup>
                          <SelectLabel>Niet ingedeeld</SelectLabel>
                          <SelectItem value={locationData.stage}>
                            {stadiumLabel(locationData.stage)}
                          </SelectItem>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {locationData.notes && (
                  <div className="pt-3 border-t">
                    <p className="font-medium text-xs text-muted-foreground">Notities</p>
                    <p className="text-muted-foreground whitespace-pre-wrap text-xs">{locationData.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Column: Sketch + Map + Parties - flex layout for alignment */}
            <div className="flex flex-col min-h-[450px]">
              {/* Top: Situatieschets + Kaart (equal height, flex-1) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
                {/* Situatieschets Upload */}
                <SiteSketchUploader
                  locationId={locationId!}
                  currentUrl={(locationData as any)?.site_sketch_url || null}
                  onUpdate={(url) => {
                    setLocationData((prev: any) => ({ ...prev, site_sketch_url: url }));
                  }}
                  className="h-full"
                />

                {/* Map Card */}
                <Card className="flex flex-col h-full">
                  <CardHeader className="py-3 px-4">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Kaart
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 p-2 min-h-0">
                    <div className="h-full w-full rounded-lg overflow-hidden min-h-[200px]">
                      {locationData.latitude && locationData.longitude ? (
                        <MapComponent
                          sites={[{
                            id: locationData.id,
                            name: locationData.name,
                            address: locationData.address || '',
                            latitude: locationData.latitude,
                            longitude: locationData.longitude,
                            site_type: 'Laadlocatie',
                          }]}
                          getMarkerColor={() => {
                            if (!locationData?.stage) return '#22c55e';
                            const stageGroup = getStageGroup(locationData.stage);
                            return STAGE_COLORS[stageGroup as keyof typeof STAGE_COLORS] || '#22c55e';
                          }}
                          portalType={userRole === 'charging_user' ? 'charging' : 'crm'}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full bg-muted rounded-lg">
                          <p className="text-sm text-muted-foreground">Geen coördinaten</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bottom: Interested Parties - fixed height, aligns with bottom of Location Details */}
              <Card className="h-16 mt-4 flex items-center px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Geïnteresseerde Partijen</span>
                    {interestedParties.length > 0 && (
                      <Badge variant="secondary" className="text-xs">{interestedParties.length}</Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {interestedParties.length === 0 ? (
                      <span className="text-xs text-muted-foreground">Geen partijen gekoppeld</span>
                    ) : (
                      <>
                        {interestedParties.slice(0, 6).map((party) => (
                          <Tooltip key={party.id}>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => navigate(`/crm/charging/opportunities/${party.opportunityId}`)}
                                className="h-8 w-8 rounded-full overflow-hidden bg-muted flex items-center justify-center hover:ring-2 hover:ring-primary transition-all"
                              >
                                {party.orgLogo ? (
                                  <img 
                                    src={party.orgLogo} 
                                    alt={party.orgName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <Building2 className="h-4 w-4 text-muted-foreground" />
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-medium">{party.orgName}</p>
                              <p className="text-xs text-muted-foreground">{party.opportunityName}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                        {interestedParties.length > 6 && (
                          <Badge variant="outline" className="text-xs">
                            +{interestedParties.length - 6}
                          </Badge>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Financiering Sectie */}
          {(locationData.ownership_type || locationData.total_investment) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Financiering
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {locationData.ownership_type && (
                    <div>
                      <p className="text-sm font-medium">Eigendomstype</p>
                      <Badge variant="secondary" className="mt-1">
                        {locationData.ownership_type === 'in_eigendom' && 'In eigendom'}
                        {locationData.ownership_type === 'koop' && 'Koop'}
                        {locationData.ownership_type === 'huur' && 'Huur'}
                        {locationData.ownership_type === 'huur_koopoptie' && 'Huur met koopoptie'}
                      </Badge>
                    </div>
                  )}
                  
                  {locationData.surface_area_m2 && (
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <Ruler className="w-4 h-4" />
                        Oppervlakte
                      </p>
                      <p className="text-lg font-semibold">{locationData.surface_area_m2.toLocaleString('nl-NL')} m²</p>
                    </div>
                  )}
                  
                  {locationData.contracted_power_kw && (
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <Zap className="w-4 h-4" />
                        Gecontracteerd vermogen
                      </p>
                      <p className="text-lg font-semibold">{locationData.contracted_power_kw.toLocaleString('nl-NL')} kW</p>
                    </div>
                  )}
                </div>

                {locationData.total_investment && (
                  <>
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium mb-2">Totale Investering</p>
                      <p className="text-2xl font-bold text-primary">
                        €{locationData.total_investment.toLocaleString('nl-NL')}
                      </p>
                    </div>

                    {locationData.equity_percentage !== null && locationData.equity_percentage !== undefined && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Equity ({locationData.equity_percentage}%)</span>
                          <span className="font-medium">
                            €{Math.round(locationData.total_investment * (locationData.equity_percentage / 100)).toLocaleString('nl-NL')}
                          </span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-3">
                          <div 
                            className="bg-primary h-3 rounded-full transition-all"
                            style={{ width: `${locationData.equity_percentage}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Financiering ({100 - locationData.equity_percentage}%)</span>
                          <span>
                            €{Math.round(locationData.total_investment * ((100 - locationData.equity_percentage) / 100)).toLocaleString('nl-NL')}
                          </span>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Kostenverdeling */}
                {(locationData.cost_land_purchase || locationData.cost_land_development || locationData.cost_charging_infra || (locationData as any).cost_engineering) && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-3">Kostenverdeling</p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {locationData.cost_land_purchase && (
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Aankoop grond</p>
                          <p className="text-lg font-semibold">€{locationData.cost_land_purchase.toLocaleString('nl-NL')}</p>
                        </div>
                      )}
                      {locationData.cost_land_development && (
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Aanleg grond</p>
                          <p className="text-lg font-semibold">€{locationData.cost_land_development.toLocaleString('nl-NL')}</p>
                        </div>
                      )}
                      {(locationData as any).cost_engineering && (
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Engineering</p>
                          <p className="text-lg font-semibold">€{(locationData as any).cost_engineering.toLocaleString('nl-NL')}</p>
                        </div>
                      )}
                      {locationData.cost_charging_infra && (
                        <div className="p-3 bg-muted/50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Laadinfrastructuur</p>
                          <p className="text-lg font-semibold">€{locationData.cost_charging_infra.toLocaleString('nl-NL')}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Project Management Section */}
          <div className="space-y-6 border-t pt-6 mt-6">
            <h2 className="text-2xl font-semibold">Projectmanagement</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LocationTimeline locationId={locationId!} />
              <LocationNotes locationId={locationId!} />
            </div>
            
            {/* To-do's - Volle breedte eronder */}
            <div className="w-full">
              <LocationTodos locationId={locationId!} />
            </div>
            
            {/* Documenten Preview */}
            <div className="w-full">
              <LocationDocumentsPreview 
                locationId={locationId!} 
                onViewAll={() => {
                  const tabsList = document.querySelector('[data-state="active"][value="overview"]');
                  const documentsTab = document.querySelector('[value="documents"]') as HTMLElement;
                  if (documentsTab) documentsTab.click();
                }}
              />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="businesscase">
          <LocationBusinesscaseFiles locationId={locationId!} />
        </TabsContent>

        <TabsContent value="activity" className="space-y-6">
          {/* T2.3 - afspraken uit de agenda die aan deze kavel hangen. */}
          <EntityAppointments scope="kavel" entityId={locationId!} />
          <LocationActivityLog locationId={locationId!} />
        </TabsContent>

        {/* T2.2 - sinds migratie 032 kan een offerte aan een Kavel hangen. */}
        <TabsContent value="quotes">
          <EntityQuotes scope="kavel" entityId={locationId!} />
        </TabsContent>

        <TabsContent value="documents">
          <LocationDocuments locationId={locationId!} />
        </TabsContent>
      </Tabs>

      <LocationModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          fetchLocationData();
        }}
        location={locationData}
      />

      <LocationSlideGenerator
        locationId={locationId!}
        isOpen={isSlideGeneratorOpen}
        onClose={() => setIsSlideGeneratorOpen(false)}
      />
    </DetailPageLayout>
  );
}
